import { useCallback } from 'react';

import { examHasResults } from 'src/services/offline/db';
import {
  getOrCreateOrderIDB,
  getOrderIDB,
} from 'src/services/offline/outboxClient';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { OrderStatusEnum } from 'src/models/GalenData/Order.model';

export default function useResumeExam() {
  const {
    updateSession,
    setLeftUrl,
    setRightUrl,
    setLeftImageAttempts,
    setRightImageAttempts,
    setLeftImageSubmission,
    setRightImageSubmission,
  } = useRemoteBoundStore();

  const initializeResumedExam = useCallback(
    async (orderId: string, siteId?: string): Promise<string | undefined> => {
      const existing = await getOrderIDB(orderId);
      const latestExam = existing?.Exams[0];
      const examId = latestExam?.ExamId ?? uuidv4();
      const graded = examHasResults(latestExam);

      const resolvedSiteId = existing?.SiteId ?? siteId;

      // Required so the exam can be started on a machine/browser that doesn't have the order in its idb
      const order = await getOrCreateOrderIDB({
        OrderId: orderId,
        OrderStatus: OrderStatusEnum.OPEN,
        ExamId: examId,
        SiteId: resolvedSiteId,
      });

      updateSession({
        iid: examId,
        orderId,
        siteId: resolvedSiteId,
        graded,
        leftImageFileName: latestExam?.LeftEyeFileName ?? '',
        rightImageFileName: latestExam?.RightEyeFileName ?? '',
        leftResults: latestExam?.LeftEyeResults ?? [],
        rightResults: latestExam?.RightEyeResults ?? [],
      });

      setLeftImageSubmission(order.LeftSubmissions);
      setRightImageSubmission(order.RightSubmissions);
      setLeftImageAttempts(order.LeftImageAttempts);
      setRightImageAttempts(order.RightImageAttempts);

      const indexedDBExam = order.Exams.find((exam) => exam.ExamId === examId);
      const leftUrl = indexedDBExam?.LeftEyeImage
        ? URL.createObjectURL(indexedDBExam.LeftEyeImage)
        : '';
      const rightUrl = indexedDBExam?.RightEyeImage
        ? URL.createObjectURL(indexedDBExam.RightEyeImage)
        : '';

      if (leftUrl) setLeftUrl(leftUrl);
      if (rightUrl) setRightUrl(rightUrl);

      return resolvedSiteId;
    },
    [
      setLeftImageSubmission,
      setRightImageSubmission,
      setLeftImageAttempts,
      setRightImageAttempts,
      setLeftUrl,
      setRightUrl,
      updateSession,
    ],
  );

  return {
    initializeResumedExam,
  };
}
